/**
 * Alluvion Home — Product Gallery + Live Frame Preview (v2)
 * =========================================================
 * Replaces Dawn's media gallery with a swipeable gallery of your lifestyle
 * images. When the customer uploads a photo, a "Your preview" slide appears
 * at the front and the gallery jumps to it. Changing the frame or mount jumps
 * back to it, and scrolls the page up to it if it's off screen (mobile).
 *
 * Which images go where is decided by ALT TEXT (Shopify admin → product → media):
 *   - Alt text matching a mockup key, e.g. "black-frame-passepartout-portrait"
 *     → used only as a preview template, never shown in the gallery
 *   - Any other alt text (e.g. "Wedding photo in a light oak frame above a sofa")
 *     → shown in the gallery, in your media order
 *
 * Needs the <script id="alv-product-json"> tag from the configurator install
 * (falls back to reading Dawn's gallery images if it's missing).
 *
 * Load order in layout/theme.liquid (all with defer):
 *   alluvion-preview.js → alluvion-frame-picker.js → alluvion-configurator.js
 */

(function () {
  'use strict';

  if (!window.location.pathname.includes('/products/print-your-own-photo')) return;

  /* ── Framing value → mockup key ──────────────────────────────────── */
  var STYLE_KEY = {
    'Print Only':                  'print-only-no-passepartout',
    'Print Only With Mount':       'print-only-passepartout',
    'Black Frame':                 'black-frame-no-passepartout',
    'Black Frame With Mount':      'black-frame-passepartout',
    'White Frame':                 'white-frame-no-passepartout',
    'White Frame With Mount':      'white-frame-passepartout',
    'Light Oak Frame':             'light-oak-frame-no-passepartout',
    'Light Oak Frame With Mount':  'light-oak-frame-passepartout',
    'Dark Oak Frame':              'dark-oak-frame-no-passepartout',
    'Dark Oak Frame With Mount':   'dark-oak-frame-passepartout'
  };

  /* ── Artwork window coordinates (% of mockup image size) ─────────── */
  var WINDOWS = {
    /* Portrait */
    'black-frame-no-passepartout-portrait':      { left: 30.0, top: 13.9, width: 39.9, height: 69.9 },
    'black-frame-passepartout-portrait':         { left: 37.1, top: 23.8, width: 25.7, height: 50.0 },
    'white-frame-no-passepartout-portrait':      { left: 30.0, top: 14.0, width: 39.9, height: 69.7 },
    'white-frame-passepartout-portrait':         { left: 37.1, top: 23.8, width: 25.7, height: 50.0 },
    'dark-oak-frame-no-passepartout-portrait':   { left: 30.0, top: 13.9, width: 39.9, height: 69.9 },
    'dark-oak-frame-passepartout-portrait':      { left: 37.1, top: 23.8, width: 25.7, height: 50.0 },
    'light-oak-frame-no-passepartout-portrait':  { left: 30.0, top: 13.9, width: 39.9, height: 69.9 },
    'light-oak-frame-passepartout-portrait':     { left: 37.1, top: 23.8, width: 25.7, height: 50.0 },
    'print-only-no-passepartout-portrait':       { left: 30.0, top: 13.9, width: 39.9, height: 69.9 },
    'print-only-passepartout-portrait':          { left: 37.1, top: 23.8, width: 25.7, height: 50.0 },
         /* Landscape — mount windows measured to the opening drawn in each template */
    'black-frame-no-passepartout-landscape':     { left: 23.1, top: 26.2, width: 55.8, height: 49.9 },
    'black-frame-passepartout-landscape':        { left: 30.8, top: 35.1, width: 40.2, height: 31.9 },
    'white-frame-no-passepartout-landscape':     { left: 23.1, top: 26.2, width: 55.8, height: 49.9 },
    'white-frame-passepartout-landscape':        { left: 30.8, top: 35.1, width: 40.2, height: 31.9 },
    'dark-oak-frame-no-passepartout-landscape':  { left: 23.2, top: 25.9, width: 55.8, height: 49.9 },
    'dark-oak-frame-passepartout-landscape':     { left: 30.8, top: 35.1, width: 40.2, height: 31.9 },
    'light-oak-frame-no-passepartout-landscape': { left: 23.2, top: 26.2, width: 55.9, height: 49.9 },
    'light-oak-frame-passepartout-landscape':    { left: 30.8, top: 35.1, width: 40.2, height: 31.9 },
    'print-only-no-passepartout-landscape':      { left: 22.3, top: 26.6, width: 55.8, height: 49.8 },
    'print-only-passepartout-landscape':         { left: 29.9, top: 35.7, width: 40.2, height: 31.9 }
  };

  /* ── State ────────────────────────────────────────────────────────── */
  var productData        = null;
  var templates          = {};   // mockup key → image URL
  var lifestyle          = [];   // [{ src, alt }]
  var currentFraming     = null;
  var currentOrientation = 'portrait';
  var photoSrc           = null;
  var activeIndex        = 0;
  var els                = {};

  /* ── Boot ─────────────────────────────────────────────────────────── */
  function init() {
    productData = readProductJson();
    collectMedia();
    currentFraming = getDawnOptionValue('Framing');
    injectStyles();
    if (!buildGallery()) return;
    bindOptionChanges();
    bindPhotoEvents();
    render();
    setActive(firstVisible());
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ── Helpers ──────────────────────────────────────────────────────── */
  function readProductJson() {
    var el = document.getElementById('alv-product-json');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function slug(s) {
    return (s || '').trim().toLowerCase().replace(/\s+/g, '-');
  }

  /* Shopify CDN URL at a given width */
  function sizedUrl(src, width) {
    if (!src) return '';
    if (src.indexOf('//') === 0) src = 'https:' + src;
    src = src.replace(/([?&])width=\d+(&|$)/, '$1').replace(/[?&]$/, '');
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function srcsetFor(src) {
    return [600, 900, 1200, 1600, 2000].map(function (w) {
      return sizedUrl(src, w) + ' ' + w + 'w';
    }).join(', ');
  }

  /* Reads the selected value of a Dawn option — works for dropdowns and pills/swatches */
  function getDawnOptionValue(name) {
    var scope = document.querySelector('variant-selects, variant-radios') || document;
    var i, list = scope.querySelectorAll('select');
    for (i = 0; i < list.length; i++) {
      if ((list[i].name || '').indexOf(name) !== -1) return list[i].value;
    }
    list = scope.querySelectorAll('input[type="radio"]:checked');
    for (i = 0; i < list.length; i++) {
      if ((list[i].name || '').indexOf(name) !== -1) return list[i].value;
    }
    return null;
  }

  /* ── Sort product media into templates vs lifestyle ──────────────── */
  function collectMedia() {
    var media = productData && productData.media ? productData.media : [];
    media.forEach(function (m) {
      if (m.media_type && m.media_type !== 'image') return;
      var src = m.src || (m.preview_image && m.preview_image.src);
      if (!src) return;
      var key = slug(m.alt);
      if (WINDOWS[key]) {
        templates[key] = sizedUrl(src, 2000);
      } else {
        lifestyle.push({ src: src, alt: m.alt || (productData && productData.title) || '' });
      }
    });
    if (!media.length) collectFromDom();
    console.log('[Alluvion Preview] ' + Object.keys(templates).length + ' templates, ' + lifestyle.length + ' gallery images');
  }

  function collectFromDom() {
    var seen = {};
    var imgs = document.querySelectorAll('media-gallery .product__media-list img, .product__media img');
    Array.prototype.forEach.call(imgs, function (img) {
      var src = bestSrc(img);
      if (!src) return;
      var base = src.split('?')[0];
      if (seen[base]) return;
      seen[base] = true;
      var key = slug(img.alt);
      if (WINDOWS[key]) templates[key] = sizedUrl(src, 2000);
      else lifestyle.push({ src: src, alt: img.alt || '' });
    });
  }

  function bestSrc(img) {
    var srcset = img.getAttribute('srcset') || img.dataset.srcset || '';
    var best = '', bestW = 0;
    srcset.split(',').forEach(function (part) {
      var p = part.trim().split(/\s+/);
      var w = parseInt(p[1] || '0', 10);
      if (p[0] && w > bestW) { bestW = w; best = p[0]; }
    });
    return best || img.getAttribute('src') || img.dataset.src || '';
  }

  /* ── Build the gallery ───────────────────────────────────────────── */
  function buildGallery() {
    var wrapper = document.querySelector('.product__media-wrapper');
    if (!wrapper) {
      console.warn('[Alluvion Preview] Could not find product gallery');
      return false;
    }
    var dawnGallery = wrapper.querySelector('media-gallery');
    if (dawnGallery) dawnGallery.style.display = 'none';

    var g = document.createElement('div');
    g.id = 'alv-gallery';
    g.innerHTML =
      '<div class="alv-g-viewport">' +
        '<div class="alv-g-track" id="alv-g-track"></div>' +
        '<button type="button" class="alv-g-arrow alv-g-arrow--prev" aria-label="Previous image">&#8249;</button>' +
        '<button type="button" class="alv-g-arrow alv-g-arrow--next" aria-label="Next image">&#8250;</button>' +
      '</div>' +
      '<div class="alv-g-thumbs" id="alv-g-thumbs"></div>';
    wrapper.appendChild(g);

    els.gallery = g;
    els.track   = g.querySelector('#alv-g-track');
    els.thumbs  = g.querySelector('#alv-g-thumbs');
    els.prev    = g.querySelector('.alv-g-arrow--prev');
    els.next    = g.querySelector('.alv-g-arrow--next');

    /* Slide 0: live preview */
    var pSlide = document.createElement('div');
    pSlide.className = 'alv-g-slide alv-g-slide--preview';
    pSlide.innerHTML =
      '<div id="alv-preview-frame">' +
        '<img id="alv-preview-mockup" src="" alt="Preview of your photo in the chosen frame" style="display:none;">' +
        '<div id="alv-preview-photo-wrap" style="display:none;">' +
          '<img id="alv-preview-photo" src="" alt="">' +
        '</div>' +
        '<div id="alv-preview-placeholder">' +
          '<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" opacity="0.3"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>' +
          '<p>Upload your photo to see a preview</p>' +
        '</div>' +
      '</div>' +
      '<span class="alv-g-badge">Your preview</span>';
    els.track.appendChild(pSlide);
    els.pSlide = pSlide;

    els.pThumb = makeThumb('', 'Your photo');
    els.pThumbImg = els.pThumb.querySelector('img');
    els.thumbs.appendChild(els.pThumb);

    /* Lifestyle slides */
    lifestyle.forEach(function (m, i) {
      var slide = document.createElement('div');
      slide.className = 'alv-g-slide';
      var img = document.createElement('img');
      img.src      = sizedUrl(m.src, 1600);
      img.srcset   = srcsetFor(m.src);
      img.sizes    = '(min-width: 990px) 55vw, 100vw';
      img.alt      = m.alt;
      img.loading  = i === 0 ? 'eager' : 'lazy';
      img.decoding = 'async';
      slide.appendChild(img);
      els.track.appendChild(slide);
      els.thumbs.appendChild(makeThumb(sizedUrl(m.src, 240), '', m.alt || 'View image ' + (i + 1)));
    });

    els.mockup      = g.querySelector('#alv-preview-mockup');
    els.photoWrap   = g.querySelector('#alv-preview-photo-wrap');
    els.photo       = g.querySelector('#alv-preview-photo');
    els.placeholder = g.querySelector('#alv-preview-placeholder');

    /* Thumbnail clicks */
    els.thumbs.addEventListener('click', function (e) {
      var btn = e.target.closest('.alv-g-thumb');
      if (!btn) return;
      goTo(Array.prototype.indexOf.call(els.thumbs.children, btn));
    });

    /* Arrows */
    els.prev.addEventListener('click', function () { step(-1); });
    els.next.addEventListener('click', function () { step(1); });

    /* Keep thumbnails in sync with swiping */
    var t;
    els.track.addEventListener('scroll', function () {
      clearTimeout(t);
      t = setTimeout(function () {
        var x = els.track.scrollLeft, best = activeIndex, bestD = Infinity;
        slides().forEach(function (s, i) {
          if (s.classList.contains('is-hidden')) return;
          var d = Math.abs(s.offsetLeft - x);
          if (d < bestD) { bestD = d; best = i; }
        });
        setActive(best);
      }, 80);
    }, { passive: true });

    return true;
  }

  function makeThumb(src, label, ariaLabel) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'alv-g-thumb';
    b.setAttribute('aria-label', ariaLabel || label || 'View image');
    var img = document.createElement('img');
    img.alt = '';
    img.loading = 'lazy';
    if (src) img.src = src;
    b.appendChild(img);
    if (label) {
      var l = document.createElement('span');
      l.className = 'alv-g-thumb__label';
      l.textContent = label;
      b.appendChild(l);
    }
    return b;
  }

  /* ── Slide navigation ────────────────────────────────────────────── */
  function slides() { return Array.prototype.slice.call(els.track.children); }

  function isVisible(i) {
    var s = slides()[i];
    return !!s && !s.classList.contains('is-hidden');
  }

  function firstVisible() {
    var list = slides();
    for (var i = 0; i < list.length; i++) if (isVisible(i)) return i;
    return 0;
  }

  function step(dir) {
    var i = activeIndex + dir;
    while (i >= 0 && i < slides().length && !isVisible(i)) i += dir;
    if (isVisible(i)) goTo(i);
  }

  function goTo(i, smooth) {
    if (!isVisible(i)) return;
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    els.track.scrollTo({
      left: slides()[i].offsetLeft,
      behavior: (smooth === false || reduce) ? 'auto' : 'smooth'
    });
    setActive(i);
  }

  function setActive(i) {
    activeIndex = i;
    var thumbs = els.thumbs.children;
    for (var n = 0; n < thumbs.length; n++) {
      var on = n === i;
      thumbs[n].classList.toggle('is-active', on);
      if (on) thumbs[n].setAttribute('aria-current', 'true');
      else thumbs[n].removeAttribute('aria-current');
    }
    /* Scroll the thumbnail strip sideways only (never the page) */
    var th = thumbs[i], c = els.thumbs;
    if (th) {
      if (th.offsetLeft < c.scrollLeft) c.scrollLeft = th.offsetLeft;
      else if (th.offsetLeft + th.offsetWidth > c.scrollLeft + c.clientWidth) {
        c.scrollLeft = th.offsetLeft + th.offsetWidth - c.clientWidth;
      }
    }
    var hasPrev = false, hasNext = false;
    for (n = 0; n < i; n++) if (isVisible(n)) hasPrev = true;
    for (n = i + 1; n < slides().length; n++) if (isVisible(n)) hasNext = true;
    els.prev.disabled = !hasPrev;
    els.next.disabled = !hasNext;
  }

  /* ── Render the live preview ─────────────────────────────────────── */
  function mockupKey() {
    var base = (currentFraming && STYLE_KEY[currentFraming]) || 'print-only-no-passepartout';
    return base + '-' + currentOrientation;
  }

  function render() {
    if (!els.mockup) return;

    var key = mockupKey();
    if (!templates[key]) key = 'print-only-no-passepartout-' + currentOrientation;
    if (!templates[key]) key = 'print-only-no-passepartout-portrait';
    var win = WINDOWS[key];
    var src = templates[key];

    if (src) {
      if (els.mockup.getAttribute('src') !== src) els.mockup.src = src;
      els.mockup.style.display = 'block';
    }

    if (win && photoSrc) {
      els.photoWrap.style.display = 'block';
      els.photoWrap.style.left    = win.left + '%';
      els.photoWrap.style.top     = win.top + '%';
      els.photoWrap.style.width   = win.width + '%';
      els.photoWrap.style.height  = win.height + '%';
      els.photo.src               = photoSrc;
      els.placeholder.style.display = 'none';
    } else {
      els.photoWrap.style.display   = 'none';
      els.placeholder.style.display = src ? 'flex' : 'none';
    }

    /* Before upload, hide the preview slide (unless there's nothing else to show) */
    var hidePreview = !photoSrc && lifestyle.length > 0;
    els.pSlide.classList.toggle('is-hidden', hidePreview);
    els.pThumb.classList.toggle('is-hidden', hidePreview);
    if (photoSrc) els.pThumbImg.src = photoSrc;

    var visibleCount = slides().filter(function (s) { return !s.classList.contains('is-hidden'); }).length;
    els.thumbs.style.display = visibleCount > 1 ? '' : 'none';
    els.gallery.classList.toggle('alv-g--single', visibleCount < 2);
  }

  /* ── Bring the preview into view (mainly mobile, where it's above the options) ── */
  function bringPreviewIntoView() {
    if (!photoSrc || !els.gallery) return;
    var r  = els.gallery.getBoundingClientRect();
    var vh = window.innerHeight || document.documentElement.clientHeight;
    /* Already comfortably on screen (e.g. desktop, where the gallery sits alongside) */
    if (r.top >= 0 && r.top < vh * 0.5) return;
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    els.gallery.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
  }

  /* ── Events ──────────────────────────────────────────────────────── */
  function bindOptionChanges() {
    document.addEventListener('change', function (e) {
      var el = e.target;
      if (!el || !el.name) return;
      /* Frame or mount changed: update the preview and show it. Size changes don't affect the preview. */
      if (el.name.indexOf('Framing') !== -1) {
        currentFraming = el.value;
        render();
        if (photoSrc) {
          goTo(0);
          bringPreviewIntoView();
        }
      }
    });
  }

  function bindPhotoEvents() {
    document.addEventListener('alv:photo-changed', function (e) {
      photoSrc           = e.detail.dataUrl;
      currentOrientation = e.detail.orientation || 'portrait';
      render();
      requestAnimationFrame(function () {
        goTo(0, false);
        bringPreviewIntoView();
      });
    });

    document.addEventListener('alv:photo-cleared', function () {
      photoSrc           = null;
      currentOrientation = 'portrait';
      render();
      goTo(firstVisible(), false);
    });
  }

  /* ── Styles ──────────────────────────────────────────────────────── */
  function injectStyles() {
    if (document.getElementById('alv-preview-styles')) return;
    var style = document.createElement('style');
    style.id  = 'alv-preview-styles';
    style.textContent = [
      '#alv-gallery { position: relative; scroll-margin-top: 9rem; }',
      '.alv-g-viewport { position: relative; }',
      '.alv-g-track { position: relative; display: flex; align-items: flex-start; overflow-x: auto; scroll-snap-type: x mandatory; scrollbar-width: none; -webkit-overflow-scrolling: touch; border-radius: var(--media-radius, 4px); }',
      '.alv-g-track::-webkit-scrollbar { display: none; }',
      '.alv-g-slide { position: relative; flex: 0 0 100%; scroll-snap-align: start; line-height: 0; }',
      '.alv-g-slide > img { display: block; width: 100%; height: auto; }',
      '.alv-g-slide.is-hidden, .alv-g-thumb.is-hidden { display: none; }',
      '.alv-g-badge { position: absolute; top: 1.2rem; left: 1.2rem; padding: .6rem 1rem; line-height: 1; font-size: 1.2rem; border-radius: 2rem; background: rgb(var(--color-background)); color: rgb(var(--color-foreground)); }',
      '.alv-g-arrow { position: absolute; top: 50%; transform: translateY(-50%); display: flex; align-items: center; justify-content: center; width: 4rem; height: 4rem; padding: 0 0 .3rem; border: 0; border-radius: 50%; background: rgba(var(--color-background), .92); color: rgb(var(--color-foreground)); font-size: 2.6rem; line-height: 1; cursor: pointer; box-shadow: 0 1px 4px rgba(0,0,0,.12); transition: opacity .15s; }',
      '.alv-g-arrow--prev { left: 1.2rem; }',
      '.alv-g-arrow--next { right: 1.2rem; }',
      '.alv-g-arrow[disabled], .alv-g--single .alv-g-arrow { opacity: 0; pointer-events: none; }',
      '.alv-g-arrow:focus-visible, .alv-g-thumb:focus-visible { outline: 2px solid rgb(var(--color-foreground)); outline-offset: 2px; }',
      '@media (hover: none) { .alv-g-arrow { display: none; } }',
      '.alv-g-thumbs { position: relative; display: flex; gap: .8rem; margin-top: 1rem; overflow-x: auto; scrollbar-width: none; }',
      '.alv-g-thumbs::-webkit-scrollbar { display: none; }',
      '.alv-g-thumb { position: relative; flex: 0 0 auto; width: 7.2rem; aspect-ratio: 1; padding: 0; overflow: hidden; border: 1px solid transparent; border-radius: var(--media-radius, 4px); background: rgba(var(--color-foreground), .04); cursor: pointer; opacity: .7; transition: opacity .15s, border-color .15s; }',
      '.alv-g-thumb:hover { opacity: 1; }',
      '.alv-g-thumb.is-active { opacity: 1; border-color: rgb(var(--color-foreground)); }',
      '.alv-g-thumb img { display: block; width: 100%; height: 100%; object-fit: cover; }',
      '.alv-g-thumb__label { position: absolute; left: 0; right: 0; bottom: 0; padding: .3rem; font-size: 1rem; line-height: 1.2; text-align: center; background: rgba(var(--color-background), .88); color: rgb(var(--color-foreground)); }',
      '#alv-preview-frame { position: relative; width: 100%; line-height: 0; overflow: hidden; background: rgb(var(--color-background)); }',
      '#alv-preview-mockup { display: block; width: 100%; height: auto; }',
      '#alv-preview-photo-wrap { position: absolute; overflow: hidden; }',
      '#alv-preview-photo { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }',
      '#alv-preview-placeholder { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: .8rem; pointer-events: none; }',
      '#alv-preview-placeholder p { margin: 0; font-size: 1.2rem; line-height: 1.4; color: rgba(var(--color-foreground), .45); font-family: var(--font-body-family); }'
    ].join('\n');
    document.head.appendChild(style);
  }

})();